import * as React from 'react';

import {formEncodedApi} from 'nutshell-core/api';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Button} from 'shells/button/button';
import {PseudoLabel} from 'shells/form/pseudo-label';
import {Popover, PopoverComponent} from 'shells/popover/popover';
import {PopoverForm} from 'shells/popover-form/popover-form';
import type {ListOptionWithCount} from 'shells/select-option-components/option-with-count';
import {Body} from 'shells/typography/body';
import {Title} from 'shells/typography/title';

import {openOauthWindow} from '../../../../oauth/open-oauth-window';

import {GraphQLSelectConstantContact} from '../../select/graphql-select-constant-contact';

import styles from './constant-contact-popover.module.css';

type Props = {
    onCancel: () => void;
    onSuccess: () => void;
    onSubmit: (data: {
        listId: string | null | undefined;
        listName: string | null | undefined;
    }) => Promise<any>;
    submitButtonText: string;
    canAccessIntegrations: boolean;
} & JSX.LibraryManagedAttributes<
    typeof PopoverComponent,
    React.ComponentProps<typeof PopoverComponent>
>;

export const ConstantContactPopover = (props: Props) => {
    const [selectedListId, setListId] = React.useState<string | null | undefined>(null);
    const [selectedListName, setListName] = React.useState<string | null | undefined>(null);
    const [isConstantContactConnected, setConstantContactConnected] = React.useState<boolean>(
        NutClientConfig.integrations && NutClientConfig.integrations.constantContactEnabled
    );
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [isSubmitComplete, setIsSubmitComplete] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);

    function handleSubmit() {
        if (!selectedListId && !selectedListName) return;

        setIsSubmitting(true);

        props
            .onSubmit({listId: selectedListId, listName: selectedListName})
            .then(() => {
                setIsSubmitComplete(true);
                setIsSubmitting(false);
            })
            .catch((err) => {
                // TODO: get an error from the server and show it.
                setIsSubmitting(false);
                if (err) {
                    setErrorMessage(safelyGetGraphQLErrorMessage(err));
                } else {
                    setErrorMessage(
                        'Sorry, something went wrong.  Not everyone was sent to Constant Contact'
                    );
                }
            });
    }

    function handleSaveComplete() {
        setIsSubmitComplete(false);
        props.onSuccess();
    }

    if (errorMessage) {
        return (
            <Popover
                anchor={props.anchor}
                isDialog={true}
                onBlur={() => {
                    setErrorMessage(null);
                    props.onCancel();
                }}
                bodyColor='error'
            >
                {errorMessage}
            </Popover>
        );
    }

    return (
        <Popover anchor={props.anchor} onBlur={props.onCancel} location={props.location}>
            {isConstantContactConnected ? (
                <PopoverForm
                    width={340}
                    onClose={props.onCancel}
                    title={
                        <div className={styles['logo-title']}>
                            <img
                                alt='Constant Contact'
                                src='/include/images/integration-logos/constant-contact.png'
                                height={16}
                                style={{marginRight: 8}}
                            />
                            Send to Constant Contact
                        </div>
                    }
                    onSubmit={handleSubmit}
                    saveButtonText={props.submitButtonText}
                    saveButtonDisabled={!selectedListId && !selectedListName}
                    isSaving={isSubmitting}
                    onClearSaveComplete={handleSaveComplete}
                    saveButtonSavingText='Sending'
                    saveButtonSuccessText='Sent!'
                >
                    <div className={styles['form-container']}>
                        <PseudoLabel>Choose a list:</PseudoLabel>
                        <GraphQLSelectConstantContact
                            selectedId={selectedListId}
                            shouldRefetch={isSubmitComplete}
                            canAccessIntegrations={props.canAccessIntegrations}
                            isCreateable={true}
                            onChange={(newValue: ListOptionWithCount) => {
                                // check for the __isNew__ constant coming back from the
                                // selectpickergenericcreatable to determine if this is an
                                // existing list
                                if (newValue.__isNew__) {
                                    setListName(newValue.label);
                                    setListId(null);
                                } else {
                                    setListId(newValue.value);
                                    setListName(null);
                                }
                            }}
                            menuPlacement='auto'
                        />
                    </div>
                </PopoverForm>
            ) : (
                <ConstantContactConnectCTA
                    canAccessIntegrations={props.canAccessIntegrations}
                    onClick={() => {
                        handleCtaClick().then(() => {
                            formEncodedApi
                                .get('/integrations/status')
                                // @ts-expect-error
                                .then((res) => res.json())
                                .then((json) => {
                                    if (
                                        json.integrationsEnabled &&
                                        json.integrationsEnabled.constantContact
                                    ) {
                                        setConstantContactConnected(true);
                                        // For the next time the popover gets opened, before a full refresh
                                        if (NutClientConfig.integrations) {
                                            NutClientConfig.integrations.constantContactEnabled =
                                                true;
                                        }
                                    } else {
                                        props.onCancel();
                                    }
                                })
                                .catch(() => {
                                    // Just close the popover if something goes wrong.
                                    props.onCancel();
                                });
                        });
                    }}
                />
            )}
        </Popover>
    );
};

function ConstantContactConnectCTA({
    canAccessIntegrations,
    onClick,
}: {
    canAccessIntegrations: boolean;
    onClick: () => void;
}) {
    return (
        <div className={styles['cta-container']}>
            <div className={styles['logo-title']}>
                <img
                    alt='Constant Contact'
                    src='/include/images/integration-logos/constant-contact.png'
                    height={42}
                    style={{marginRight: 8}}
                />
                <Title>Add Nutshell people to your Constant Contact lists!</Title>
            </div>
            <Body>
                Connect to Constant Contact to easily send your selected Nutshell people to any of
                your lists.
            </Body>
            {canAccessIntegrations ? (
                <React.Fragment>
                    <Button variant='primary' onClick={onClick} size='big'>
                        Connect to Constant Contact
                    </Button>
                </React.Fragment>
            ) : (
                <Body>
                    <i>
                        Ask your administrator to set up your Constant Contact integration to enable
                        this feature!
                    </i>
                </Body>
            )}
        </div>
    );
}

function handleCtaClick() {
    const windowFeatures = {
        width: 490,
        height: 600,
        toolbar: 'no',
        location: 'no',
        alwaysRaised: 'yes',
    } as const;

    return openOauthWindow(
        '/setup/instant-integration/edit-constant-contact?redirect=%2Fsetup%2Fintegration%2Fconstant-contact-popup-callback',
        'Constant Contact',
        windowFeatures
    );
}
